import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { Dropdown, FormControl, FormGroup } from 'react-bootstrap';
import styled from 'styled-components';

import 'video-react/dist/video-react.css';
import { Tag, Tags } from '../../../interfaces';
import { cancelInputEvent, hash } from '../../../utils';
import { EventTagDropdownOption } from './interfaces';
import { prepareListOfDropdownOptions } from './prepareListOfDropdownOptions';

const StyledDropdownItem = styled(Dropdown.Item)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

interface Props {
  onTagClick: (
    tag: Tag,
    path: string,
    value?: any,
    tags?: Tags,
    setTags?: (tags: Tags) => void,
    onUserTagChange?: (tags: Tags) => void,
  ) => (evt?: SyntheticEvent | any) => void;
  questions: Tag[];
  tags?: Tags;
  setTags?: (tags: Tags) => void;
  onUserTagChange?: (tags: Tags) => void;
  dataTestId?: string;
}

export function Autocomplete(props: Props) {
  const { questions, onTagClick, tags, setTags, onUserTagChange, dataTestId } = props;

  const [value, setValue] = useState<string>('');
  const [optionList, setOptionList] = useState<EventTagDropdownOption[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<EventTagDropdownOption[]>([]);
  const [dropdownShow, setDropdownShow] = useState<boolean>(false);
  const toggleRef = useRef();
  const inputRef = useRef();

  const handleTextInput = (e: any) => {
    const { value } = e.target;
    setValue(value);
    if (value) {
      setDropdownShow(true);
    }
  };

  const handleClick = (tag: Tag, path: string, value?: Tags[0]) => (evt?: SyntheticEvent | any) => {
    onTagClick(tag, `td:${path}`, value, tags, setTags, onUserTagChange)(evt);
    setValue('');
    setDropdownShow(false);
  };

  const handleEnterKey = (evt: KeyboardEvent) => {
    if (evt.key === 'Enter' && filteredOptions.length > 0) {
      const option = filteredOptions[0];
      handleClick(option.tag, option.path, option.value)();
    }

    if (evt.key === 'Escape') {
      setDropdownShow(false);
    }

    cancelInputEvent(evt);
  };

  useEffect(() => {
    const suggestions = prepareListOfDropdownOptions(questions);
    setOptionList(suggestions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions]);

  useEffect(() => {
    // filter "questions" by "value" using partial consecutive letters match like in VS code search bar
    const regexp = new RegExp(value.split('').join('.*'), 'i');
    const filteredOptions: EventTagDropdownOption[] = value
      ? optionList.filter((option) => regexp.test(option.name))
      : optionList;
    setFilteredOptions(filteredOptions);
  }, [value, optionList]);

  useEffect(() => {
    const element = inputRef.current as any;
    element.addEventListener('keyup', handleEnterKey);
    return () => element.removeEventListener('keyup', handleEnterKey);
  }, [inputRef?.current, filteredOptions]);

  return (
    <FormGroup>
      <FormControl
        className="hdn-input"
        value={value}
        onChange={handleTextInput}
        onFocus={() => setDropdownShow(true)}
        onBlur={() => setTimeout(() => setDropdownShow(false), 200)}
        data-test={dataTestId}
        placeholder="Select or start typing"
        ref={inputRef}
      />

      <Dropdown show={dropdownShow}>
        <Dropdown.Toggle
          variant="link"
          id="dropdown-basic"
          className="hdn-events-filter--license-plate__dropdown-toggle"
          ref={toggleRef}
        />

        <Dropdown.Menu
          className="hdn-events-filter--license-plate__dropdown"
          popperConfig={{
            strategy: 'fixed',
            modifiers: [
              {
                name: 'offset',
                options: { offset: [-300, 0] },
              },
            ],
          }}
        >
          {filteredOptions.map((option) => (
            <StyledDropdownItem onClick={handleClick(option.tag, option.path, option.value)} key={hash(option)}>
              {option.name}
            </StyledDropdownItem>
          ))}

          {filteredOptions.length === 0 && (
            <StyledDropdownItem>There are no items matching your search.</StyledDropdownItem>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </FormGroup>
  );
}
